import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  OperatorRole,
  Business,
  ROLE_PERMISSIONS,
  authenticateOperator,
  getCurrentOperator,
  storeOperatorSession,
  logoutOperator,
  getBusinesses,
} from './operator-api';

// Re-export Business type for components to use
export type { Business };

const SELECTED_BUSINESS_KEY = 'selectedBusinessId';

interface OperatorContextType {
  operator: { id: string; role: OperatorRole; name: string } | null;
  isLoading: boolean;
  error: string | null;
  login: (operatorId: string) => Promise<boolean>;
  logout: () => void;
  hasPermission: (permission: keyof typeof ROLE_PERMISSIONS.ADMIN) => boolean;
  permissions: typeof ROLE_PERMISSIONS.ADMIN | null;
  // Business selection
  businesses: Business[];
  selectedBusinessId: string | null;
  setSelectedBusinessId: (id: string | null) => void;
  selectedBusiness: Business | null;
}

const OperatorContext = createContext<OperatorContextType | undefined>(undefined);

export function OperatorProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [operator, setOperator] = useState<{ id: string; role: OperatorRole; name: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBusinessId, setSelectedBusinessIdState] = useState<string | null>(null);

  // Persist selectedBusinessId to sessionStorage
  const setSelectedBusinessId = useCallback((id: string | null) => {
    setSelectedBusinessIdState(id);
    if (typeof window !== 'undefined') {
      if (id) {
        sessionStorage.setItem(SELECTED_BUSINESS_KEY, id);
      } else {
        sessionStorage.removeItem(SELECTED_BUSINESS_KEY);
      }
    }
  }, []);

  // Compute selectedBusiness from selectedBusinessId
  const selectedBusiness = useMemo(() => {
    if (!selectedBusinessId) return null;
    return businesses.find(b => b.id === selectedBusinessId) || null;
  }, [selectedBusinessId, businesses]);

  // Load businesses when operator changes
  const loadBusinesses = useCallback(async () => {
    if (!operator) {
      setBusinesses([]);
      return;
    }

    try {
      const loadedBusinesses = await getBusinesses();
      setBusinesses(loadedBusinesses);

      // Restore selected business from sessionStorage
      if (typeof window !== 'undefined') {
        const savedBusinessId = sessionStorage.getItem(SELECTED_BUSINESS_KEY);
        if (savedBusinessId && loadedBusinesses.some(b => b.id === savedBusinessId)) {
          setSelectedBusinessIdState(savedBusinessId);
        } else if (loadedBusinesses.length > 0) {
          // Auto-select first business if none saved or saved one not accessible
          setSelectedBusinessId(loadedBusinesses[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to load businesses:', err);
      setBusinesses([]);
    }
  }, [operator, setSelectedBusinessId]);

  // Check for existing session on mount
  // Bridges AuthProvider (localStorage) → OperatorProvider (sessionStorage)
  useEffect(() => {
    let existingOperator = getCurrentOperator();

    // Fallback: if sessionStorage is empty, try AuthProvider's localStorage data
    // This bridges the dev login / WorkOS OAuth flow (which stores in localStorage)
    // with the OperatorProvider (which reads from sessionStorage)
    if (!existingOperator && typeof window !== 'undefined') {
      try {
        const authUserRaw = localStorage.getItem('auth_user');
        if (authUserRaw) {
          const authUser = JSON.parse(authUserRaw);
          if (authUser.id && authUser.role && authUser.name) {
            storeOperatorSession(
              authUser.id,
              authUser.role as OperatorRole,
              authUser.name,
              authUser.businessIds || []
            );
            existingOperator = getCurrentOperator();
          }
        }
      } catch {
        // Ignore parse errors
      }
    }

    if (existingOperator && existingOperator.id && existingOperator.role && existingOperator.name) {
      setOperator(existingOperator);
    }
    setIsLoading(false);
  }, []);

  // Load businesses when operator is set
  useEffect(() => {
    if (operator) {
      loadBusinesses();
    }
  }, [operator, loadBusinesses]);

  // Redirect to login if not authenticated and on a protected path
  useEffect(() => {
    if (!isLoading && !operator) {
      // In the Vite SPA, we check the current URL pathname directly
      const pathname = window.location.pathname;
      if (pathname.startsWith('/operator') && pathname !== '/sign-in') {
        navigate({ to: '/sign-in' });
      }
    }
  }, [isLoading, operator, navigate]);

  const login = useCallback(async (operatorId: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await authenticateOperator(operatorId);

      if (result) {
        setOperator({ id: result.id, role: result.role, name: result.name });
        return true;
      } else {
        setError('Operator not found or inactive');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    logoutOperator();
    setOperator(null);
    setBusinesses([]);
    setSelectedBusinessId(null);
    navigate({ to: '/sign-in' });
  }, [navigate, setSelectedBusinessId]);

  const hasPermission = useCallback((permission: keyof typeof ROLE_PERMISSIONS.ADMIN): boolean => {
    if (!operator) return false;
    return ROLE_PERMISSIONS[operator.role][permission];
  }, [operator]);

  const permissions = operator ? ROLE_PERMISSIONS[operator.role] : null;

  return (
    <OperatorContext.Provider
      value={{
        operator,
        isLoading,
        error,
        login,
        logout,
        hasPermission,
        permissions,
        businesses,
        selectedBusinessId,
        setSelectedBusinessId,
        selectedBusiness,
      }}
    >
      {children}
    </OperatorContext.Provider>
  );
}

export function useOperator() {
  const context = useContext(OperatorContext);
  if (context === undefined) {
    throw new Error('useOperator must be used within an OperatorProvider');
  }
  return context;
}
